// 앱 전체 화면 표시 날짜의 SSoT. 화면에 그리는 날짜·시각은 이 모듈의 함수만 쓴다(앱 언어 설정
// getCurrentLocaleTag 기준, 부재/비정상 값은 폴백 '-'). 시간대는 브라우저 로컬.
// 서버 전송·저장·키·정렬·엑셀/PDF/파일명용 문자열은 대상이 아니다(표시 전용).
// licenseHelper 는 formatLicenseDate/formatLicenseDateOnly 를 이 모듈로 위임한다.
import { getCurrentLocaleTag } from '../config/currentLang';

// 날짜만 문자열('YYYY-MM-DD', 'YYYY-MM')은 new Date(str) 가 UTC 자정으로 읽어 음수 시간대에서
// 하루 전으로 보이므로 로컬 자정으로 만든다. 월·일 범위 밖(예 '2026-13-40')은 null.
const parseDateOnly = (s) => {
    let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    let y;
    let mo;
    let day;
    if (m) {
        [y, mo, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
    } else {
        m = /^(\d{4})-(\d{2})$/.exec(s);
        if (!m) return undefined; // 날짜만 형식 아님 → 호출부가 new Date(v) 로 처리
        [y, mo, day] = [Number(m[1]), Number(m[2]), 1];
    }
    if (mo < 1 || mo > 12 || day < 1 || day > 31) return null;
    const d = new Date(y, mo - 1, day);
    // 2026-02-31 처럼 넘치는 날짜는 다른 달로 밀리므로 거부.
    if (d.getMonth() !== mo - 1 || d.getDate() !== day) return null;
    return d;
};

// 공통 검증 헬퍼 — 유효하면 Date 객체, 아니면 null.
// ⚠️ 반환은 null 이다. '-' 등 표시용 폴백 문자열은 아래 공개 함수들의 몫이다.
const resolveDisplayDate = (v) => {
    if (v === null || v === undefined) return null;
    let d;
    if (typeof v === 'string') {
        const s = v.trim();
        if (!s || s === '-') return null;
        const dateOnly = parseDateOnly(s);
        if (dateOnly === null) return null;
        d = dateOnly !== undefined ? dateOnly : new Date(v);
    } else {
        if (typeof v === 'number' && (!Number.isFinite(v) || v <= 0)) return null;
        d = new Date(v);
    }
    const ts = d.getTime();
    if (!Number.isFinite(ts) || ts <= 0) return null;
    return d;
};

// 다섯 공개 함수 공통 틀: 가드 → 로케일 포맷 → 실패 시 fallback.
// locale 인자 없으면 브라우저 로케일로 렌더돼 앱 언어와 표기가 불일치한다(IMP-438).
// Intl 미지원/잘못된 locale tag 는 RangeError 를 던질 수 있어 try/catch 로 흰 화면을 막는다.
const render = (name, v, fallback, format) => {
    const d = resolveDisplayDate(v);
    if (!d) return fallback;
    try {
        return format(d, getCurrentLocaleTag());
    } catch (e) {
        console.warn(`[dateDisplay] ${name} failed`, e);
        return fallback;
    }
};

// 입력 허용: 양의 ms timestamp, ISO 8601 문자열, Date, moment, 'YYYY-MM-DD'/'YYYY-MM'(로컬 자정).
// 거부: null/undefined/'' / 숫자 0·음수·NaN·Infinity / '-' / 공백만 / 변환 실패 → fallback.
// opts.fallback — 거부 시 반환 문자열(기본 '-'). 호출부가 '-' 비교로 유효성을 되묻지 않게 노출한다.

// 날짜+시각. 기본은 초까지(toLocaleString 그대로), seconds:false 면 분까지.
export const formatDisplayDateTime = (v, { fallback = '-', seconds = true } = {}) =>
    render('formatDisplayDateTime', v, fallback, (d, tag) =>
        seconds
            ? d.toLocaleString(tag)
            : d.toLocaleString(tag, { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    );

// 날짜만(시각 제외).
export const formatDisplayDate = (v, { fallback = '-' } = {}) =>
    render('formatDisplayDate', v, fallback, (d, tag) => d.toLocaleDateString(tag));

// 시각만(분까지).
export const formatDisplayTime = (v, { fallback = '-' } = {}) =>
    render('formatDisplayTime', v, fallback, (d, tag) => d.toLocaleTimeString(tag, { hour: 'numeric', minute: '2-digit' }));

// 월/일(연도 없음, 차트 축 등).
export const formatDisplayMonthDay = (v, { fallback = '-' } = {}) =>
    render('formatDisplayMonthDay', v, fallback, (d, tag) => d.toLocaleDateString(tag, { month: 'numeric', day: 'numeric' }));

// 연/월.
export const formatDisplayYearMonth = (v, { fallback = '-' } = {}) =>
    render('formatDisplayYearMonth', v, fallback, (d, tag) => d.toLocaleDateString(tag, { year: 'numeric', month: 'numeric' }));
